import React from "react";
import { Formik, Field, FieldProps } from "formik";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Heading,
  VStack,
  Stack,
  Textarea,
  Checkbox,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "@chakra-ui/react";
import axios from "axios";
import API from "../../config/api";

export default function CreateNotification() {
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();

  return (
    <Flex h="100vh" direction={"column"}>
      <Heading as="h4" size="lg" mb="4">
        {t("notification.title.new")}
      </Heading>
      <Box bg="white" rounded="md" w={360}>
        <Formik
          initialValues={{
            recipients: "users",
            title: "",
            description: "",
            reference: "",
            scheduled_send_time: new Date().toDateString(),
            force_send: false,
          }}
          onSubmit={async (values, { setErrors }) => {
            setSubmitting(true);
            await axios.post<void>(API.NOTIFICATION, values);
            toast({
              title: t("notification.api.created_success"),
              status: "success",
              isClosable: true,
            });
            setSubmitting(false);
            navigate(-1);
          }}
        >
          {({ handleSubmit, setFieldValue, errors, touched }) => (
            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="flex-start">
                <FormControl
                  isRequired
                  isInvalid={!!errors.title && touched.title}
                >
                  <FormLabel htmlFor="title">
                    {t("notification.title.col_name")}
                  </FormLabel>
                  <Field as={Input} id="title" name="title" />
                  <FormErrorMessage>{errors.title}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!errors.description && touched.description}
                >
                  <FormLabel htmlFor="description">
                    {t("notification.description")}
                  </FormLabel>
                  <Field
                    as={Textarea}
                    id="description"
                    name="description"
                  ></Field>
                  <FormErrorMessage>{errors.description}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!errors.reference && touched.reference}
                >
                  <FormLabel htmlFor="reference">
                    {t("notification.reference")}
                  </FormLabel>
                  <Field as={Input} id="reference" name="reference" />
                  <FormErrorMessage>{errors.reference}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isRequired
                  isInvalid={
                    !!errors.scheduled_send_time && touched.scheduled_send_time
                  }
                >
                  <FormLabel htmlFor="scheduled_send_time">
                    {t("notification.scheduled_send_time")}
                  </FormLabel>
                  <Field
                    as={Input}
                    type="datetime-local"
                    id="scheduled_send_time"
                    name="scheduled_send_time"
                  />
                  <FormErrorMessage>
                    {errors.scheduled_send_time}
                  </FormErrorMessage>
                </FormControl>
                <Field name="force_send">
                  {({ field }: FieldProps) => {
                    return (
                      <FormControl>
                        <Checkbox
                          id="force_send"
                          name="force_send"
                          colorScheme="blue"
                          isChecked={field.value}
                          onChange={() =>
                            setFieldValue(field.name, !field.value)
                          }
                        >
                          {t("notification.force_send")}
                        </Checkbox>
                      </FormControl>
                    );
                  }}
                </Field>
                <Stack direction="row" spacing={4} align="center">
                  <Button
                    colorScheme="blue"
                    variant="solid"
                    type="submit"
                    isLoading={submitting}
                    loadingText={t("action.loading.creating")}
                  >
                    {t("action.create")}
                  </Button>
                  <Button
                    colorScheme="blue"
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    disabled={submitting}
                  >
                    {t("action.cancel")}
                  </Button>
                </Stack>
              </VStack>
            </form>
          )}
        </Formik>
      </Box>
    </Flex>
  );
}
